'use client';

import React, { useEffect, useMemo, useRef, useState } from 'react';

function usePrefersReducedMotion() {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setPrefersReducedMotion(media.matches);
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);

  return prefersReducedMotion;
}

function useInViewOnce<T extends Element>(options?: IntersectionObserverInit) {
  const ref = useRef<T | null>(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === 'undefined') {
      setIsInView(true);
      return;
    }

    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (!entry) return;
      if (entry.isIntersecting) {
        setIsInView(true);
        observer.disconnect();
      }
    }, options);

    observer.observe(el);
    return () => observer.disconnect();
  }, [options]);

  return { ref, isInView } as const;
}

interface AnimatedTextProps {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}

const AnimatedText: React.FC<AnimatedTextProps> = ({ children, className = '', delay = 0 }) => {
  const prefersReducedMotion = usePrefersReducedMotion();
  const { ref, isInView } = useInViewOnce<HTMLDivElement>({
    root: null,
    rootMargin: '-50px',
    threshold: 0.01,
  });

  const style = useMemo<React.CSSProperties>(() => {
    if (prefersReducedMotion) return {};
    return {
      transitionDelay: `${delay}s`,
    };
  }, [delay, prefersReducedMotion]);

  return (
    <div
      ref={ref}
      style={style}
      className={[
        className,
        prefersReducedMotion
          ? ''
          : 'transition-all duration-500 ease-out will-change-transform will-change-opacity',
        prefersReducedMotion
          ? ''
          : isInView
            ? 'opacity-100 translate-y-0'
            : 'opacity-0 translate-y-4',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {children}
    </div>
  );
};

interface AnimatedHTMLProps {
  html: string;
  className?: string;
  delay?: number;
}

export const AnimatedHTML: React.FC<AnimatedHTMLProps> = ({ html, className = '', delay = 0 }) => {
  const prefersReducedMotion = usePrefersReducedMotion();
  const { ref, isInView } = useInViewOnce<HTMLDivElement>({
    root: null,
    rootMargin: '-50px',
    threshold: 0.01,
  });

  const style = useMemo<React.CSSProperties>(() => {
    if (prefersReducedMotion) return {};
    return {
      transitionDelay: `${delay}s`,
    };
  }, [delay, prefersReducedMotion]);

  return (
    <div
      ref={ref}
      style={style}
      className={[
        className,
        prefersReducedMotion
          ? ''
          : 'transition-all duration-500 ease-out will-change-transform will-change-opacity',
        prefersReducedMotion
          ? ''
          : isInView
            ? 'opacity-100 translate-y-0'
            : 'opacity-0 translate-y-4',
      ]
        .filter(Boolean)
        .join(' ')}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
};

export default AnimatedText;
